import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Heart, MessageCircle, Share2, ArrowLeft, Loader2, Calendar, MapPin, Link as LinkIcon } from 'lucide-react';
import { getInitials, formatNumber } from '../lib/utils';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";
const SITE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      // Try to get user info from public posts
      const postsRes = await axios.get(`${API}/public/posts?limit=50`);
      const userPosts = postsRes.data.filter(p => p.user?.username === username);
      
      if (userPosts.length > 0) {
        setProfile(userPosts[0].user);
        setPosts(userPosts);
      } else {
        setError('User not found');
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="font-semibold">Profile</span>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-xl font-semibold mb-2">User not found</p>
          <p className="text-muted-foreground mb-4">@{username} doesn't exist or has no public posts.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{profile.display_name || profile.username} (@{profile.username}) | FityH</title>
        <meta name="description" content={`Follow ${profile.display_name || profile.username} on FityH - Connect. Share. Create. Earn.`} />
        <meta property="og:title" content={`${profile.display_name || profile.username} (@{profile.username}) | FityH`} />
        <meta property="og:description" content={`Follow ${profile.display_name || profile.username} on FityH - Connect. Share. Create. Earn.`} />
        <meta property="og:image" content={profile.avatar || LOGO_URL} />
        <meta property="og:url" content={`${SITE_URL}/@${profile.username}`} />
        <meta property="og:type" content="profile" />
        <meta property="twitter:card" content="summary" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <p className="font-semibold">{profile.display_name || profile.username}</p>
                <p className="text-xs text-muted-foreground">{posts.length} posts</p>
              </div>
            </div>
            <Button onClick={() => navigate('/register')} className="rounded-full">
              Follow
            </Button>
          </div>
        </header>

        {/* Profile Info */}
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-start gap-4 mb-6">
            <Avatar className="w-20 h-20 ring-2 ring-primary/50">
              <AvatarImage src={profile.avatar} />
              <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                {getInitials(profile.display_name || profile.username)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <h1 className="text-xl font-bold">{profile.display_name || profile.username}</h1>
              <p className="text-muted-foreground">@{profile.username}</p>
              
              {profile.bio && (
                <p className="mt-2">{profile.bio}</p>
              )}
              
              <div className="flex gap-4 mt-3 text-sm">
                <span><strong>{posts.length}</strong> posts</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex gap-2 mb-6">
            <Button className="flex-1 rounded-full" onClick={() => navigate('/register')}>
              Follow
            </Button>
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => navigate('/register')}>
              Message
            </Button>
          </div>

          {/* Posts */}
          <h2 className="font-semibold mb-4">Posts</h2>
          <div className="space-y-4">
            {posts.map((post) => (
              <article 
                key={post.id}
                onClick={() => navigate(`/p/${post.id}`)}
                className="bg-card rounded-xl p-4 border border-border/50 cursor-pointer hover:border-primary/50 transition-colors"
              >
                <p className="mb-3">{post.content}</p>
                
                {post.media && post.media.length > 0 && (
                  <div className="rounded-lg overflow-hidden mb-3">
                    <img 
                      src={post.media[0]} 
                      alt="" 
                      className="w-full max-h-64 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-muted-foreground text-sm">
                  <span className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    {formatNumber(post.likes_count)}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" />
                    {formatNumber(post.comments_count)}
                  </span>
                  <span className="ml-auto">{formatTimeAgo(post.created_at)}</span>
                </div>
              </article>
            ))}
          </div>

          {/* Join CTA */}
          <div className="text-center py-8 mt-4 border-t border-border/50">
            <p className="text-muted-foreground mb-4">Join FityH to interact with {profile.display_name || profile.username}</p>
            <Button onClick={() => navigate('/register')} className="rounded-full px-8">
              Sign Up Free
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
